import { createFileRoute } from "@tanstack/react-router";
import { useAgent } from "@/hooks/use-agents";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  IconArrowLeft,
  IconUser,
  IconMail,
  IconPhone,
  IconHash,
  IconDeviceSim,
  IconActivity,
  IconTrendingUp,
  } from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/agents/$id/")({
  component: AgentDetailsPage,
});

function AgentDetailsPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AgentDetailsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentDetailsContent() {
  const { id } = Route.useParams();
  const { data: agent, isLoading, error } = useAgent(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading agent details...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <IconUser className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive font-medium">Failed to load agent</p>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </p>
        </div>
      </div>
    );
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "suspended":
        return "secondary";
      case "deleted":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/agents">
          <Button variant="ghost" size="sm">
            <IconArrowLeft className="h-4 w-4 mr-2" />
            Back to Agents
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{(agent as any)?.agentName}</h1>
          <p className="text-muted-foreground">Agent Management</p>
        </div>
      </div>

      {/* Agent Overview */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <IconActivity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge variant={getStatusBadgeVariant((agent as any)?.status)}>
              {(agent as any)?.status}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">
              {(agent as any)?.status === "active" ? "Currently active" : "Not active"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Serial Number</CardTitle>
            <IconHash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(agent as any)?.serialNumber}</div>
            <p className="text-xs text-muted-foreground">Agent ID</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commission Rate</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{(agent as any)?.commissionRate}%</div>
            <p className="text-xs text-muted-foreground">Current rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Member Since</CardTitle>
            <IconActivity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDistanceToNow(new Date((agent as any)?.createdAt || ''), {
                addSuffix: true,
              })}
            </div>
            <p className="text-xs text-muted-foreground">Account age</p>
          </CardContent>
        </Card>
      </div>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>
            Agent's contact and account details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <IconUser className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">Full Name</p>
                <p className="text-sm text-muted-foreground">{(agent as any)?.agentName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <IconMail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-muted-foreground">{(agent as any)?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <IconPhone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">Phone</p>
                <p className="text-sm text-muted-foreground">{(agent as any)?.phone || "Not provided"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <IconHash className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">Username</p>
                <p className="text-sm text-muted-foreground">{(agent as any)?.username}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconDeviceSim className="h-5 w-5" />
              SIM Statistics
            </CardTitle>
            <CardDescription>
              SIM cards and device information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium">Total SIMs</p>
                <p className="text-2xl font-bold">{(agent as any)?.stats?.totalSIMs || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Active SIMs</p>
                <p className="text-2xl font-bold text-green-600">{(agent as any)?.stats?.activeSIMs || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Total Devices</p>
                <p className="text-2xl font-bold">{(agent as any)?.stats?.totalDevices || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Online Devices</p>
                <p className="text-2xl font-bold text-green-600">{(agent as any)?.stats?.onlineDevices || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconTrendingUp className="h-5 w-5" />
              Performance Statistics
            </CardTitle>
            <CardDescription>
              Transaction and commission performance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium">Total Transactions</p>
                <p className="text-2xl font-bold">{(agent as any)?.stats?.totalTransactions || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Success Rate</p>
                <p className="text-2xl font-bold text-green-600">{(agent as any)?.stats?.successRate || 0}%</p>
              </div>
              <div>
                <p className="text-sm font-medium">Commission Earned</p>
                <p className="text-2xl font-bold text-green-600">৳{(agent as any)?.stats?.commissionEarned || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Commission Pending</p>
                <p className="text-2xl font-bold text-orange-600">৳{(agent as any)?.stats?.commissionPending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common management actions for this agent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Link to="/agents/$id/sims" params={{ id }}>
              <Button variant="outline">
                <IconDeviceSim className="h-4 w-4 mr-2" />
                View SIMs
              </Button>
            </Link>
            <Link to="/agents/$id/edit" params={{ id }}>
              <Button variant="outline">
                <IconActivity className="h-4 w-4 mr-2" />
                Edit Agent
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}